'use client';

import { useState } from 'react';
import type { CfooterContactFormProps } from './CfooterContactForm';
import { ContactFormStep } from './footerModels';
import { useFooterContactFields } from './useFooterContactFields';
import { useFooterContactSubmission } from './useFooterContactSubmission';

interface UseFooterContactFormInput {
  onContactClick?: () => void;
}

export interface FooterContactFormController extends CfooterContactFormProps {
  committedEmail: string;
  committedMessage: string;
}

export function useFooterContactForm({
  onContactClick,
}: UseFooterContactFormInput): FooterContactFormController {
  const [contactStep, setContactStep] = useState<ContactFormStep>(ContactFormStep.BeforeStep);
  const [committedEmail, setCommittedEmail] = useState('');
  const [committedMessage, setCommittedMessage] = useState('');
  const fields = useFooterContactFields();
  const submission = useFooterContactSubmission({
    fields,
    setContactStep,
    setCommittedMessage,
    onContactClick,
  });

  const handleStartContactForm = (): void => setContactStep(ContactFormStep.NameStep);
  const handleBackStep = (): void => {
    if (contactStep === ContactFormStep.NameStep) {
      setContactStep(ContactFormStep.BeforeStep);
    } else if (contactStep > ContactFormStep.BeforeStep) {
      setContactStep(contactStep - 1);
    }
  };
  const handleNameProceed = (): void => {
    if (!fields.validateName()) {
      return;
    }
    setContactStep(ContactFormStep.EmailStep);
  };

  const handleEmailProceed = (): void => {
    if (!fields.validateEmail()) {
      return;
    }
    setCommittedEmail(fields.email.trim());
    setContactStep(ContactFormStep.MessageStep);
  };
  const handleErrorBackToName = (): void => setContactStep(ContactFormStep.NameStep);

  return {
    contactStep,
    name: fields.name,
    email: fields.email,
    message: fields.message,
    nameError: fields.nameError,
    emailError: fields.emailError,
    messageError: fields.messageError,
    isSubmitting: submission.isSubmitting,
    onStartContactForm: handleStartContactForm,
    onNameChange: fields.handleNameChange,
    onEmailChange: fields.handleEmailChange,
    onMessageChange: fields.handleMessageChange,
    onNameProceed: handleNameProceed,
    onEmailProceed: handleEmailProceed,
    onSubmit: submission.handleSubmit,
    onBackStep: handleBackStep,
    onErrorBackToName: handleErrorBackToName,
    committedEmail,
    committedMessage,
  };
}
